/**
 * The one aggregation behind every training-hours figure on the L&D dashboard.
 * 共用此函式，**勿另寫彙總**：多份 copy 會導致數字分歧——例如只有一份排除
 * trainer 自己的列，同一期間就會在兩個 tab 報出不同的參加人數。
 * The Overview tab and the Training Hours tab both call this.
 *
 * Inputs are the raw /api/ld/attendance and /api/ld/cpd-records arrays; the
 * period is applied here so "which rows count" is decided in one place too.
 *
 * Three independent hour buckets — internal mandatory
 * (sessions flagged isMandatory — the flag, not the category, decides),
 * internal non-mandatory, and External CPD. They are reported side by side and
 * never offset each other; only the mandatory bucket counts towards the
 * yearly target.
 */

/** A trainer's row is not an attendance: 0 training hours, so counting
 *  it would add a 0-hour "participant" and drag every average down. Teaching
 *  hours are reported separately — see `trainer` in the returned object. */
export const isTraineeRow = (record) => !record.isTrainer;

/**
 * Inclusive period test. An unparseable date is kept rather than silently
 * dropped, and the end date covers its whole day.
 */
export const inPeriod = (dateStr, startDate, endDate) => {
  if (!startDate && !endDate) return true;
  const d = dateStr ? new Date(dateStr) : null;
  if (!d || isNaN(d.getTime())) return true;
  if (startDate && d < new Date(startDate)) return false;
  if (endDate) {
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    if (d > end) return false;
  }
  return true;
};

const blankPerson = (name, email, position, department, grade) => ({
  name: name || email.split('@')[0],
  email,
  position,
  department,
  grade,
  mandatoryHours: 0,
  nonMandatoryHours: 0,
  externalHours: 0,
  totalHours: 0,
  internalSessions: 0,
  externalSessions: 0,
});

/**
 * @param {Array} attendance - /api/ld/attendance rows
 * @param {Array} cpdRecords - /api/ld/cpd-records rows
 * @param {string} startDate - 'YYYY-MM-DD' or '' for no lower bound
 * @param {string} endDate - 'YYYY-MM-DD' or '' for no upper bound
 */
export function computeTrainingStats(attendance = [], cpdRecords = [], startDate = '', endDate = '') {
  // Attendance is dated by the session; CPD by the certificate, not a session.
  const filteredAttendance = attendance.filter((r) => inPeriod(r.date, startDate, endDate));
  const filteredCpdRecords = cpdRecords.filter((r) => inPeriod(r.certificateDate, startDate, endDate));

  const byEmail = {};
  filteredAttendance.filter(isTraineeRow).forEach((record) => {
    const email = record.email;
    if (!email) return;
    if (!byEmail[email]) {
      byEmail[email] = blankPerson(record.name, email, record.position, record.department, record.grade);
    } else {
      byEmail[email].name = byEmail[email].name || record.name;
      byEmail[email].position = byEmail[email].position || record.position;
      byEmail[email].department = byEmail[email].department || record.department;
      byEmail[email].grade = byEmail[email].grade || record.grade;
    }
    const hours = record.trainingHours || 0;
    if (record.isMandatory) byEmail[email].mandatoryHours += hours;
    else byEmail[email].nonMandatoryHours += hours;
    byEmail[email].totalHours += hours;
    byEmail[email].internalSessions += 1;
  });

  filteredCpdRecords.forEach((record) => {
    const email = record.participantEmail;
    if (!email) return;
    if (!byEmail[email]) {
      byEmail[email] = blankPerson(record.participantName, email, record.position, record.department, record.grade);
    } else {
      byEmail[email].name = record.participantName || byEmail[email].name;
      byEmail[email].position = record.position || byEmail[email].position;
      byEmail[email].department = byEmail[email].department || record.department;
      byEmail[email].grade = byEmail[email].grade || record.grade;
    }
    const hours = record.hours || 0;
    byEmail[email].externalHours += hours;
    byEmail[email].totalHours += hours;
    byEmail[email].externalSessions += 1;
  });

  const peopleArray = Object.values(byEmail);
  const totalMandatoryHours = peopleArray.reduce((s, p) => s + p.mandatoryHours, 0);
  const totalNonMandatoryHours = peopleArray.reduce((s, p) => s + p.nonMandatoryHours, 0);
  const totalExternalHours = peopleArray.reduce((s, p) => s + p.externalHours, 0);
  const totalHours = totalMandatoryHours + totalNonMandatoryHours + totalExternalHours;

  // The summary table groups the same people three ways — designation,
  // department, grade (the block is switchable). Same buckets,
  // same denominator, only the grouping key changes, so the three views can
  // never disagree with each other or with the participant list.
  const summarise = (keyOf) => {
    const buckets = {};
    peopleArray.forEach((person) => {
      const group = keyOf(person) || 'Unknown';
      if (!buckets[group]) {
        buckets[group] = {
          group,
          count: 0,
          totalHours: 0,
          mandatoryHours: 0,
          nonMandatoryHours: 0,
          externalHours: 0,
        };
      }
      buckets[group].count += 1;
      buckets[group].totalHours += person.totalHours;
      buckets[group].mandatoryHours += person.mandatoryHours;
      buckets[group].nonMandatoryHours += person.nonMandatoryHours;
      buckets[group].externalHours += person.externalHours;
    });
    return Object.values(buckets)
      .map((b) => ({ ...b, avgHours: b.count > 0 ? b.totalHours / b.count : 0 }))
      .sort((a, b) => b.avgHours - a.avgHours);
  };

  const summaries = {
    designation: summarise((p) => p.position),
    department: summarise((p) => p.department),
    grade: summarise((p) => p.grade),
  };

  // Trainer hours — the other side of the same rows. A trainer books no
  // training hours for teaching, so their work is invisible on every figure
  // above; this is where it is reported. Every trainer row counts as a
  // session taught, whichever role and whatever the factor (an assistant at
  // ×0 taught it too); the lead / assistant split rides along.
  const trainerByEmail = {};
  filteredAttendance.filter((r) => !isTraineeRow(r)).forEach((record) => {
    const email = record.email;
    if (!email) return;
    if (!trainerByEmail[email]) {
      trainerByEmail[email] = {
        name: record.name || email.split('@')[0],
        email,
        position: record.position,
        department: record.department,
        sessions: 0,
        leadSessions: 0,
        assistantSessions: 0,
        trainerHours: 0,
      };
    }
    trainerByEmail[email].sessions += 1;
    if (record.trainerRole === 'assistant') trainerByEmail[email].assistantSessions += 1;
    else trainerByEmail[email].leadSessions += 1;
    trainerByEmail[email].trainerHours += record.trainerHours || 0;
  });
  const trainerPeople = Object.values(trainerByEmail);

  return {
    filteredAttendance,
    filteredCpdRecords,
    totalMandatoryHours,
    totalNonMandatoryHours,
    totalExternalHours,
    totalHours,
    avgHoursPerPerson: peopleArray.length > 0 ? totalHours / peopleArray.length : 0,
    uniquePeople: peopleArray.length,
    peopleArray,
    summaries,
    topPerformers: peopleArray.slice().sort((a, b) => b.totalHours - a.totalHours).slice(0, 10),
    trainer: {
      people: trainerPeople,
      totalHours: trainerPeople.reduce((s, p) => s + p.trainerHours, 0),
      totalSessions: trainerPeople.reduce((s, p) => s + p.sessions, 0),
    },
  };
}
